/**
 * Payout Request Form Component
 * Form to request a payout
 */

import { useState } from 'react';
import { affiliateApi } from '../../api/affiliate';

interface PayoutRequestFormProps {
  availableAmount: number;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function PayoutRequestForm({
  availableAmount,
  onSuccess,
  onCancel,
}: PayoutRequestFormProps) {
  const [formData, setFormData] = useState({
    amount: availableAmount,
    paymentMethod: 'paypal' as 'paypal' | 'bank' | 'check' | 'crypto',
    paymentEmail: '',
    paymentDetails: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.amount < 50) {
      setError('Minimum payout amount is $50.00');
      return;
    }

    if (formData.amount > availableAmount) {
      setError(`Amount cannot exceed available balance of $${availableAmount.toFixed(2)}`);
      return;
    }

    if (formData.paymentMethod === 'paypal' && !formData.paymentEmail) {
      setError('PayPal email is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await affiliateApi.requestPayout({
        amount: formData.amount,
        paymentMethod: formData.paymentMethod,
        paymentEmail: formData.paymentEmail || undefined,
        paymentDetails: formData.paymentDetails || undefined,
      });

      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to request payout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-900/20 border border-red-600 rounded-lg p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Amount */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Payout Amount
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-slate-400">$</span>
          </div>
          <input
            type="number"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            step="0.01"
            min="50"
            max={availableAmount}
            required
            className="w-full bg-slate-700/50 text-white rounded-lg pl-8 pr-4 py-2 border border-gray-600 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
          />
        </div>
        <p className="text-sm text-slate-400 mt-1">
          Available: ${availableAmount.toFixed(2)} (Minimum: $50.00)
        </p>
      </div>

      {/* Payment Method */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Payment Method
        </label>
        <select
          name="paymentMethod"
          value={formData.paymentMethod}
          onChange={handleChange}
          required
          className="w-full bg-slate-700/50 text-white rounded-lg px-4 py-2 border border-gray-600 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
        >
          <option value="paypal">PayPal</option>
          <option value="bank">Bank Transfer</option>
          <option value="check">Check</option>
          <option value="crypto">Cryptocurrency</option>
        </select>
      </div>

      {/* PayPal Email */}
      {formData.paymentMethod === 'paypal' && (
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            PayPal Email
          </label>
          <input
            type="email"
            name="paymentEmail"
            value={formData.paymentEmail}
            onChange={handleChange}
            required
            placeholder="your-paypal@email.com"
            className="w-full bg-slate-700/50 text-white rounded-lg px-4 py-2 border border-gray-600 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
          />
        </div>
      )}

      {/* Bank Details */}
      {formData.paymentMethod === 'bank' && (
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Bank Account Details
          </label>
          <textarea
            name="paymentDetails"
            value={formData.paymentDetails}
            onChange={handleChange}
            required
            rows={4}
            placeholder="Bank name, account number, routing number, etc."
            className="w-full bg-slate-700/50 text-white rounded-lg px-4 py-2 border border-gray-600 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
          />
        </div>
      )}

      {/* Crypto Address */}
      {formData.paymentMethod === 'crypto' && (
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Cryptocurrency Wallet Address
          </label>
          <textarea
            name="paymentDetails"
            value={formData.paymentDetails}
            onChange={handleChange}
            required
            rows={3}
            placeholder="Your wallet address and cryptocurrency type"
            className="w-full bg-slate-700/50 text-white rounded-lg px-4 py-2 border border-gray-600 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
          />
        </div>
      )}

      {/* Check Address */}
      {formData.paymentMethod === 'check' && (
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Mailing Address
          </label>
          <textarea
            name="paymentDetails"
            value={formData.paymentDetails}
            onChange={handleChange}
            required
            rows={4}
            placeholder="Full mailing address for check delivery"
            className="w-full bg-slate-700/50 text-white rounded-lg px-4 py-2 border border-gray-600 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
          />
        </div>
      )}

      {/* Notice */}
      <div className="bg-orange-900/20 border border-orange-700 rounded-lg p-4">
        <p className="text-sm text-orange-300">
          <strong>Note:</strong> Payout requests are reviewed by our team and
          typically processed within 3-5 business days. You'll receive an email
          notification when your payout is processed.
        </p>
      </div>

      {/* Actions */}
      <div className="flex space-x-3">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {loading ? 'Submitting...' : 'Submit Payout Request'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-6 py-3 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
